'use client';

import { format, isValid, parse } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import * as React from 'react';

import { cn } from '@/lib/utils';
import { Button } from './button';
import { Calendar } from './calendar';
import { Input } from './input';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

export interface DateInputProps {
	/** Date value in ISO format (YYYY-MM-DD) */
	value?: string;
	/** Called with ISO date string (YYYY-MM-DD) or empty string */
	onChange?: (value: string) => void;
	/** Placeholder text for the input */
	placeholder?: string;
	/** Whether the input is disabled */
	disabled?: boolean;
	/** Additional class name */
	className?: string;
}

const DATE_FORMAT = 'dd/MM/yyyy';
const ISO_FORMAT = 'yyyy-MM-dd';

function parseIsoToDate(iso: string): Date | undefined {
	if (!iso) return undefined;
	const d = parse(iso, ISO_FORMAT, new Date());
	return isValid(d) ? d : undefined;
}

function formatDateDisplay(iso: string): string {
	const d = parseIsoToDate(iso);
	return d ? format(d, DATE_FORMAT) : iso;
}

/**
 * A date input that supports both manual text entry (dd/MM/yyyy) and
 * a calendar dropdown for visual selection.
 */
const DateInput = React.forwardRef<HTMLInputElement, DateInputProps>(
	({ value = '', onChange, placeholder, disabled, className }, ref) => {
		const [open, setOpen] = React.useState(false);
		const [textValue, setTextValue] = React.useState(() =>
			formatDateDisplay(value)
		);
		const inputRef = React.useRef<HTMLInputElement>(null);

		// Merge forwarded ref with internal ref
		React.useImperativeHandle(ref, () => inputRef.current!);

		// Sync text when value prop changes externally
		React.useEffect(() => {
			setTextValue(formatDateDisplay(value));
		}, [value]);

		const emitChange = React.useCallback(
			(iso: string) => {
				onChange?.(iso);
			},
			[onChange]
		);

		// Handle manual text input
		const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
			const raw = e.target.value;
			setTextValue(raw);

			// Try to parse as dd/MM/yyyy
			const parsed = parse(raw, DATE_FORMAT, new Date());
			if (isValid(parsed) && raw.length === 10) {
				emitChange(format(parsed, ISO_FORMAT));
			} else if (raw === '') {
				emitChange('');
			}
		};

		// Commit on blur — reformat if valid, clear if empty
		const handleBlur = () => {
			if (textValue === '') {
				emitChange('');
				return;
			}
			const parsed = parse(textValue, DATE_FORMAT, new Date());
			if (isValid(parsed)) {
				const iso = format(parsed, ISO_FORMAT);
				emitChange(iso);
				setTextValue(format(parsed, DATE_FORMAT));
			} else {
				// Revert to last valid value
				setTextValue(formatDateDisplay(value));
			}
		};

		// Handle calendar selection
		const handleCalendarSelect = (day: Date | undefined) => {
			if (day) {
				const iso = format(day, ISO_FORMAT);
				emitChange(iso);
				setTextValue(format(day, DATE_FORMAT));
			}
			setOpen(false);
		};

		const selectedDate = parseIsoToDate(value);

		return (
			<div className={cn('relative flex items-center', className)}>
				<Input
					ref={inputRef}
					type="text"
					inputMode="numeric"
					value={textValue}
					onChange={handleTextChange}
					onBlur={handleBlur}
					placeholder={placeholder ?? 'dd/mm/yyyy'}
					disabled={disabled}
					className="pr-9"
				/>
				<Popover open={open} onOpenChange={setOpen}>
					<PopoverTrigger asChild>
						<Button
							type="button"
							variant="ghost"
							size="sm"
							disabled={disabled}
							className="absolute right-0 h-full px-2 text-muted-foreground hover:text-foreground"
							tabIndex={-1}
						>
							<CalendarIcon className="w-4 h-4" />
						</Button>
					</PopoverTrigger>
					<PopoverContent className="w-auto p-0" align="end">
						<Calendar
							mode="single"
							selected={selectedDate}
							onSelect={handleCalendarSelect}
							defaultMonth={selectedDate}
							initialFocus
						/>
					</PopoverContent>
				</Popover>
			</div>
		);
	}
);
DateInput.displayName = 'DateInput';

export { DateInput };
